import React from "react";
import { Box } from "braid-design-system";
import Loading from "../components/Loading";
import VendorListTable from "../components/Vendor/VendorListTable";
import CreateUpdateVendorDrawer, { VendorFormData, VendorToEdit } from "../components/Vendor/CreateUpdateVendorDrawer";
import { createVendor, getAllVendors, updateVendor, VendorCreateUpdateRequest, VendorResponse } from "../services/vendorService";
import { useToast } from "../hooks/useToast";
import { useCanManageMasters } from "../hooks/useCanManageMasters";

const toRequestPayload = (formData: VendorFormData): VendorCreateUpdateRequest => ({
    ven_accno: formData.ven_accno,
    ven_code: formData.ven_code,
    ven_Name: formData.ven_Name,
    ven_titile: formData.ven_titile,
    ven_groupid: formData.ven_groupid,
    ven_active: formData.ven_active,
    ven_add1: formData.ven_add1,
    ven_add2: formData.ven_add2,
    ven_add3: formData.ven_add3,
    ven_add4: formData.ven_add4,
    ven_tep1: formData.ven_tep1,
    ven_tep2: formData.ven_tep2,
    ven_email: formData.ven_email,
    ven_contact: formData.ven_contact,
    ven_reordercycle: formData.ven_reordercycle,
    ven_creditlimit: formData.ven_creditlimit,
    ven_creditperiod: formData.ven_creditperiod,
    ven_opbal: formData.ven_opbal,
    ven_purchase: formData.ven_purchase,
    ven_returns: formData.ven_returns,
    ven_creditnote: formData.ven_creditnote,
    ven_debitnote: formData.ven_debitnote,
    ven_payment: formData.ven_payment,
    ven_balance: formData.ven_balance,
    ven_taxregno: formData.ven_taxregno,
    ven_latitude: formData.ven_latitude,
    ven_longitude: formData.ven_longitude,
});

const VendorManagement: React.FC = () => {
    const toast = useToast();
    const canManageMasters = useCanManageMasters();
    const [loading, setLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string | null>(null);
    const [vendorList, setVendorList] = React.useState<VendorResponse[]>([]);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [selectedVendor, setSelectedVendor] = React.useState<VendorToEdit | undefined>(undefined);

    const fetchVendors = React.useCallback(async () => {
        try {
            setLoading(true);
            setLoadingMessage("Please wait, Fetching vendors...");
            const data = await getAllVendors();
            setVendorList(data);
        } catch (error: any) {
            toast.showError(`Error fetching vendors: ${error?.message || "Unknown error"}`);
            setVendorList([]);
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    }, [toast]);

    React.useEffect(() => {
        fetchVendors();
    }, [fetchVendors]);

    const handleSaveVendor = React.useCallback(async (vendorData: VendorFormData, editingVendor?: VendorToEdit) => {
        if (editingVendor?.ven_id) {
            const payload = {
                ...toRequestPayload(vendorData),
                ven_opbal: editingVendor.ven_opbal ?? null,
                ven_balance: editingVendor.ven_balance ?? null,
            };
            await updateVendor(editingVendor.ven_id, payload);
        } else {
            const payload = toRequestPayload(vendorData);
            await createVendor(payload);
        }
    }, []);

    const handleEditVendor = React.useCallback((vendor: VendorResponse) => {
        setSelectedVendor({
            ...vendor,
            ven_id: vendor.ven_id ?? undefined,
            ven_active: Boolean(vendor.ven_active),
        });
        setDrawerOpen(true);
    }, []);

    const handleCloseDrawer = React.useCallback(() => {
        setDrawerOpen(false);
        setSelectedVendor(undefined);
    }, []);

    return (
        <>
            {loading && <Loading message={loadingMessage || "Loading..."} />}

            <Box padding="medium">
                <VendorListTable
                    vendorList={vendorList}
                    onCreateVendor={canManageMasters ? () => setDrawerOpen(true) : undefined}
                    onEditVendor={handleEditVendor}
                />
            </Box>

            <CreateUpdateVendorDrawer
                isOpen={drawerOpen}
                onClose={handleCloseDrawer}
                vendorToEdit={selectedVendor}
                onSave={handleSaveVendor}
                onVendorSaved={fetchVendors}
                isReadOnly={!canManageMasters}
            />
        </>
    );
};

export default VendorManagement;
